import React from 'react';
import { Box, Text, Flex, Divider, Badge } from '@chakra-ui/react';
import AudioPlayer from '../../components/page-components/call-activity/AudioPlayer';

const Details = ({ callData }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        }).format(date);
    };

    const formatDuration = (seconds) => {
        if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) return '';
        const sec = Math.floor(seconds % 60);
        const min = Math.floor((seconds / 60) % 60);
        const hr = Math.floor(seconds / 3600);
        const pad = (n) => n.toString().padStart(2, '0');
        if (hr > 0) {
            return `${hr}:${pad(min)}:${pad(sec)}`;
        }
        return `${pad(min)}:${pad(sec)}`;
    };

    const formatUSPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        return phoneNumber;
    };

    return (
        <Box>
            <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Call Details</Text>
            {/* Contact Info */}
            <Flex justify="space-between" align="center" mb={4}>
                <Box>
                    <Text fontSize="lg" fontWeight="bold">
                        {callData.contact_info?.name || 'Unknown Caller'}
                    </Text>
                    <Text fontSize="md" color="gray.600">
                        {formatUSPhoneNumber(callData.caller)}
                    </Text>
                    {callData.contact_info?.email && (
                        <Text fontSize="sm" color="blue.500">
                            {callData.contact_info.email}
                        </Text>
                    )}
                </Box>
                <Box textAlign="right">
                    <Text fontSize="sm" color="gray.500">
                        {formatDate(callData.start_time)}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                        Duration: {formatDuration(callData.call_duration)}
                    </Text>
                </Box>
            </Flex>

            <Divider mb={4} />

            {/* Audio Player */}
            {callData.recording_uri && (
                <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Recording:</Text>
                    <AudioPlayer audioSrc={`${process.env.REACT_APP_SERVER_API_URL}/rest_endpoints/call_recording_p/${callData.call_sid}`} />
                </Box>
            )}

            {/* Transcript */}
            {callData.transcript && (
                <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Transcript:</Text>
                    <Box
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        maxH="200px"
                        overflowY="auto"
                        whiteSpace="pre-wrap"
                        fontSize="sm"
                    >
                        {callData.transcript}
                    </Box>
                </Box>
            )}

            {/* Summary */}
            {callData.summary && (
                <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Summary:</Text>
                    <Text fontSize="sm">{callData.summary}</Text>
                </Box>
            )}

            {/* Extracted Call Details */}
            {callData.extracted_call_details && (
                <Box mb={4}>
                    {callData.extracted_call_details.notes && (
                        <Box mb={2}>
                            <Text fontSize="sm" fontWeight="medium">Notes:</Text>
                            <Text fontSize="sm">{callData.extracted_call_details.notes}</Text>
                        </Box>
                    )}
                    {callData.extracted_call_details.feedback && (
                        <Box mb={2}>
                            <Text fontSize="sm" fontWeight="medium">Feedback:</Text>
                            <Text fontSize="sm">{callData.extracted_call_details.feedback}</Text>
                        </Box>
                    )}
                    {callData.extracted_call_details.meta_data?.recommended_actions && (
                        <Box>
                            <Text fontSize="sm" fontWeight="medium">Recommended Actions:</Text>
                            <ul>
                                {callData.extracted_call_details.meta_data.recommended_actions.map((action, index) => (
                                    <li key={index} style={{ fontSize: '14px' }}>{action}</li>
                                ))}
                            </ul>
                        </Box>
                    )}
                </Box>
            )}

            {/* Event Booked */}
            {callData.event_booked && (
                <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Event Booked:</Text>
                    <Text fontSize="sm">{JSON.stringify(callData.event_booked, null, 2)}</Text>
                </Box>
            )}

            {/* Status */}
            <Flex gap={2}>
                {callData.is_delete && <Badge colorScheme="red">Deleted</Badge>}
                {callData.is_blocked && <Badge colorScheme="red">Blocked</Badge>}
            </Flex>
        </Box>
    );
};

export default Details;